export type Experience = "ADMIN" | "CENTER" | "WALLET" | "VERIFY";

export interface RuntimeDomain {
  experience: Experience | "API";
  hostname: string;
  url: string;
  authRequired: boolean;
}

export interface RuntimeConfig {
  environment: string;
  experience: Experience;
  baseDomain: string;
  domains: Record<string, RuntimeDomain>;
  supabaseUrl: string;
  supabasePublishableKey: string;
}

function localExperience(baseDomain: string): Experience {
  const preview = new URLSearchParams(window.location.search).get("experience")?.toUpperCase();
  if (preview === "ADMIN" || preview === "CENTER" || preview === "WALLET" || preview === "VERIFY") {
    return preview;
  }

  const hostname = window.location.hostname.toLowerCase();
  const suffix = `.${baseDomain.toLowerCase()}`;

  if (hostname.endsWith(suffix)) {
    const prefix = hostname.slice(0, -suffix.length);
    if (prefix === "admin") return "ADMIN";
    if (prefix === "center") return "CENTER";
    if (prefix === "wallet") return "WALLET";
    if (prefix === "verify") return "VERIFY";
  }

  const value = String(import.meta.env.VITE_APP_EXPERIENCE || "CENTER").toUpperCase();
  if (value === "ADMIN" || value === "CENTER" || value === "WALLET" || value === "VERIFY") {
    return value;
  }

  return "CENTER";
}

function buildLocalDomains(baseDomain: string): Record<string, RuntimeDomain> {
  return {
    admin: { experience: "ADMIN", hostname: `admin.${baseDomain}`, url: `https://admin.${baseDomain}`, authRequired: true },
    center: { experience: "CENTER", hostname: `center.${baseDomain}`, url: `https://center.${baseDomain}`, authRequired: true },
    wallet: { experience: "WALLET", hostname: `wallet.${baseDomain}`, url: `https://wallet.${baseDomain}`, authRequired: true },
    verify: { experience: "VERIFY", hostname: `verify.${baseDomain}`, url: `https://verify.${baseDomain}`, authRequired: false },
    api: { experience: "API", hostname: `api.${baseDomain}`, url: `https://api.${baseDomain}`, authRequired: true }
  };
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const preview = new URLSearchParams(window.location.search).get("experience");
  const endpoint = preview
    ? `/runtime-config?experience=${encodeURIComponent(preview)}`
    : "/runtime-config";

  try {
    const response = await fetch(endpoint, { credentials: "same-origin" });
    if (response.ok) {
      return (await response.json()) as RuntimeConfig;
    }
  } catch {
    // Vite/local development falls through to compile-time configuration.
  }

  const baseDomain = String(import.meta.env.VITE_BASE_DOMAIN || "daily-coach-ing.com");
  const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || "");
  const supabasePublishableKey = String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "");

  if (!supabaseUrl || !supabasePublishableKey) {
    throw new Error("Supabase runtime configuration is missing.");
  }

  return {
    environment: String(import.meta.env.VITE_APP_ENV || "DEVELOPMENT"),
    experience: localExperience(baseDomain),
    baseDomain,
    domains: buildLocalDomains(baseDomain),
    supabaseUrl,
    supabasePublishableKey
  };
}
